import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Box, Flex, SimpleGrid } from '@chakra-ui/react';
import { FormControl } from '../FormControl';
import { FormLabel } from '../FormLabel';
import { FormErrorMessage } from '../FormErrorMessage';
import { Input } from './Input';
import { InputGroup } from './InputGroup';
import { InputRightElement } from './InputRightElement';
import { IconButton } from '../IconButton';
import Typography from 'src/components/Typography';

import { ReactComponent as Eye } from 'src/assets/icons/eye.svg';
import { ReactComponent as EyeClosed } from 'src/assets/icons/eye-closed.svg';
import { getStringColor } from 'src/theme/colors';

interface PasswordRule {
  key: string;
  label: string;
  validator: (value: string) => boolean;
}

const passwordRules: PasswordRule[] = [
  {
    key: 'minLength',
    label: 'pelo menos 8 caracteres',
    validator: (value) => value.length >= 8,
  },
  {
    key: 'hasNumber',
    label: 'pelo menos 1 número',
    validator: (value) => /\d/.test(value),
  },
  {
    key: 'hasLetter',
    label: 'pelo menos 1 letra',
    validator: (value) => /[a-zA-Z]/.test(value),
  },
  {
    key: 'hasSymbol',
    label: 'pelo menos 1 símbolo',
    validator: (value) => /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(value),
  },
];

function getBarColor(strengthLevel: number, barIndex: number): string {
  if (strengthLevel === 0) {
    return 'gray.200';
  }

  switch (strengthLevel) {
    case 1:
      return barIndex === 1 ? 'notification.error' : 'gray.200';
    case 2:
      return barIndex <= 2 ? '#FFDE49' : 'gray.200';
    case 3:
      return 'notification.success';
    default:
      return 'gray.200';
  }
}

function getLabelColor(strengthLevel: number): string {
  switch (strengthLevel) {
    case 1:
      return 'notification.error';
    case 2:
      return 'warning.light';
    case 3:
      return 'notification.success';
    default:
      return 'gray.200';
  }
}

interface RuleIndicatorProps {
  isValid: boolean;
  label: string;
  showError?: boolean;
}

function RuleIndicator({ isValid, label, showError }: RuleIndicatorProps) {
  const invalidColor = showError ? 'notification.error' : 'primary.1000';

  return (
    <Flex align={'center'} gap={'8px'}>
      <Box
        w={'10px'}
        h={'10px'}
        borderRadius={'50%'}
        bg={isValid ? 'notification.success' : showError ? 'notification.error' : 'transparent'}
        border={isValid || showError ? 'none' : '2px solid'}
        borderColor={isValid ? 'transparent' : invalidColor}
        transition={'all 0.3s ease'}
      />
      <Typography model={'body'} size={'small'} weight={'regular'} color={invalidColor}>
        {label}
      </Typography>
    </Flex>
  );
}

interface PasswordWithValidationProps {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  onValidationChange?: (isValid: boolean) => void;
  errorMessage?: string;
  isInvalid?: boolean;
  name?: string;
  placeholder?: string;
  autoFocus?: boolean;
}

export function PasswordWithValidation(props: PasswordWithValidationProps) {
  const { label, value, onChange, onBlur, onValidationChange, errorMessage, isInvalid, name, placeholder, autoFocus } =
    props;

  const [showPassword, setShowPassword] = useState(false);
  const prevIsAllValidRef = useRef<boolean | null>(null);
  const onValidationChangeRef = useRef(onValidationChange);

  useEffect(() => {
    onValidationChangeRef.current = onValidationChange;
  });

  const validationStatus = useMemo(() => {
    return passwordRules.map((rule) => ({
      ...rule,
      isValid: rule.validator(value),
    }));
  }, [value]);

  const isAllValid = useMemo(() => {
    return validationStatus.every((rule) => rule.isValid);
  }, [validationStatus]);

  const validRulesCount = useMemo(() => {
    return validationStatus.filter((rule) => rule.isValid).length;
  }, [validationStatus]);

  const strengthLevel = useMemo(() => {
    if (validRulesCount === 0) return 0;
    if (validRulesCount <= 2) return 1;
    if (validRulesCount === 3) return 2;
    return 3;
  }, [validRulesCount]);

  const strengthLabel = useMemo(() => {
    switch (strengthLevel) {
      case 1:
        return 'FRACA';
      case 2:
        return 'MÉDIA';
      case 3:
        return 'FORTE';
      default:
        return '';
    }
  }, [strengthLevel]);

  useEffect(() => {
    if (prevIsAllValidRef.current !== isAllValid) {
      prevIsAllValidRef.current = isAllValid;
      if (onValidationChangeRef.current) {
        onValidationChangeRef.current(isAllValid);
      }
    }
  }, [isAllValid]);

  const handleBlur = useCallback(() => {
    if (onBlur) {
      onBlur();
    }
  }, [onBlur]);

  return (
    <FormControl isInvalid={isInvalid}>
      <FormLabel htmlFor={name} textTransform={'uppercase'}>
        {label}
      </FormLabel>
      <InputGroup>
        <Input
          value={value}
          h={'45px'}
          id={name}
          name={name}
          type={showPassword ? 'text' : 'password'}
          placeholder={placeholder || 'Digite aqui a senha'}
          onBlur={handleBlur}
          onChange={onChange}
          autoComplete="off"
          autoFocus={autoFocus}
          border={'1px solid'}
          borderColor={getStringColor('primary.100')}
          borderRadius={'10px'}
          _placeholder={{ color: getStringColor('support.dark'), opacity: 0.5 }}
          color="support.dark"
          backgroundColor="support.surface"
          focusBorderColor="primary.100"
          fontFamily={'Ambit'}
          fontSize={'12px'}
          errorBorderColor="notification.error"
        />
        <InputRightElement h={'100%'}>
          <IconButton
            _focus={{}}
            onClick={() => setShowPassword(!showPassword)}
            mr={'10px'}
            variant={'ghost'}
            borderRadius={'50%'}
            size={'sm'}
            aria-label="show-password"
          >
            {showPassword ? <Eye /> : <EyeClosed />}
          </IconButton>
        </InputRightElement>
      </InputGroup>

      <Box mt="10px">
        <Flex gap="8px" align="center">
          <Flex flex={1} gap="6px">
            <Box flex={1} h="6px" borderRadius="3px" bg={getBarColor(strengthLevel, 1)} transition="all 0.3s ease" />
            <Box flex={1} h="6px" borderRadius="3px" bg={getBarColor(strengthLevel, 2)} transition="all 0.3s ease" />
            <Box flex={1} h="6px" borderRadius="3px" bg={getBarColor(strengthLevel, 3)} transition="all 0.3s ease" />
          </Flex>
        </Flex>
        <Flex gap="6px" mt="4px">
          <Flex flex={1} justify="flex-start">
            {strengthLevel === 1 && (
              <Typography model="caption" weight="bold" color={getLabelColor(1)}>
                FRACA
              </Typography>
            )}
          </Flex>
          <Flex flex={1} justify="flex-start">
            {strengthLevel === 2 && (
              <Typography model="caption" weight="bold" color={getLabelColor(2)}>
                MÉDIA
              </Typography>
            )}
          </Flex>
          <Flex flex={1} justify="flex-start">
            {strengthLevel === 3 && (
              <Typography model="caption" weight="bold" color={getLabelColor(3)}>
                FORTE
              </Typography>
            )}
          </Flex>
        </Flex>
      </Box>

      {isInvalid && !isAllValid && (
        <Typography model="body" size="small" weight="regular" color="notification.error">
          Critérios para criação de senha não atendidos
        </Typography>
      )}

      <SimpleGrid columns={2} gap="10px" mt="15px">
        {validationStatus.map((rule) => (
          <RuleIndicator
            key={rule.key}
            isValid={rule.isValid}
            label={rule.label}
            showError={isInvalid && !isAllValid}
          />
        ))}
      </SimpleGrid>

      {isInvalid && isAllValid && errorMessage && (
        <FormErrorMessage textTransform={'uppercase'}>{errorMessage}</FormErrorMessage>
      )}
    </FormControl>
  );
}

export { passwordRules };
